<script setup lang="ts">
import { computed, useAttrs } from '#imports';
import { NuxtLink } from '#components';

type linkColor = 'black' | 'blue';
type linkVariation = 'icon' | 'underline';

type Props = {
	tag?: 'button' | 'a';
	color?: linkColor;
	variations?: Array<linkVariation>;
	disabled?: boolean;
};

const attrs = useAttrs();

const props = withDefaults(defineProps<Props>(), {
	tag: 'a',
	color: 'black',
	variations: () => [],
	disabled: false,
});

const component = computed(() => {
	return Object.keys(attrs).includes('to') ? NuxtLink : props.tag;
});

const computedClass = computed(() => {
	const modificators = props.variations.map((variation) => {
		return `v-link--${variation}`;
	});

	const classes = {
		'v-link': true,
		[`v-link--${props.color}`]: props.color,
		'v-link--disabled': props.disabled,
		'button': props.tag === 'button',
	};

	return [classes, ...modificators];
});
</script>

<template>
	<component :is="component" :class="computedClass">
		<span v-if="$slots.prefix" class="v-link__prefix">
			<slot name="prefix" />
		</span>

		<span class="v-link__content">
			<slot />
		</span>

		<span v-if="$slots.sufix" class="v-link__sufix">
			<slot name="sufix" />
		</span>
	</component>
</template>

<style lang="scss" scoped>
.v-link {
	$size: 16px;

	display: inline-flex;
	align-items: center;

	cursor: pointer;
	transition: color $transition-function $transition-duration;

	&--disabled {
		cursor: not-allowed;

		color: $dove-gray-1 !important;
	}

	&--black {
		color: $black;

		@include hover {
			color: $dove-gray;
		}
	}

	&--blue {
		color: $malibu;

		@include hover {
			color: $science-blue;
		}
	}

	&--icon & {
		&__content {
			width: rem($size);
			height: rem($size);
		}
	}

	&--underline {
		text-decoration: underline;
	}

	&__prefix {
		flex-shrink: 0;

		width: rem($size);
		height: rem($size);
		margin-right: rem(12px);
	}

	&__content {
		flex-grow: 1;
	}

	&__sufix {
		flex-shrink: 0;

		width: rem($size);
		height: rem($size);
		margin-left: rem(12px);
	}
}
</style>
